import type { MarketCategory, MarketItem, MarketSeller } from '../types/market'

/**
 * Development data. Counts here describe seeded inventory, not production
 * activity — the data-mode banner says so on every surface.
 */

export const marketCategories: MarketCategory[] = [
  {
    id: 'food',
    group: 'shop',
    label: 'Food & groceries',
    blurb: 'Meals, snacks, groceries and everyday essentials.',
    pattern: 'ordering',
    countLabel: '18 vendors seeded',
    to: '/market/food',
    accent: 'amber',
  },
  {
    id: 'books',
    group: 'shop',
    label: 'Books & supplies',
    blurb: 'Textbooks, past questions, stationery and lab materials.',
    pattern: 'goods',
    countLabel: '64 items seeded',
    to: '/market/books',
    accent: 'blue',
  },
  {
    id: 'products',
    group: 'shop',
    label: 'Campus products',
    blurb: 'Electronics, clothing, accessories and student essentials.',
    pattern: 'goods',
    countLabel: '41 items seeded',
    to: '/market/products',
    accent: 'violet',
  },
  {
    id: 'accommodation',
    group: 'live',
    label: 'Accommodation',
    blurb: 'Hostels, apartments, rooms and inspected housing.',
    pattern: 'property',
    countLabel: '23 places seeded',
    to: '/market/accommodation',
    handedTo: {
      domain: 'Housing',
      to: '/housing',
      why: 'Applications, inspections, rent records and roommate matching all live in Housing. Marketplace only helps you find a place.',
    },
    accent: 'green',
  },
  {
    id: 'transport',
    group: 'live',
    label: 'Transport',
    blurb: 'Campus rides, moving, delivery and vehicle hire.',
    pattern: 'booking',
    countLabel: '11 providers seeded',
    to: '/market/transport',
    accent: 'orange',
  },
  {
    id: 'services',
    group: 'services',
    label: 'Services',
    blurb: 'Academic, creative, technical and business services.',
    pattern: 'booking',
    countLabel: '37 providers seeded',
    to: '/market/services',
    accent: 'teal',
  },
  {
    id: 'gigs',
    group: 'services',
    label: 'Gigs',
    blurb: 'Student-to-student work. Offer something, or hire someone.',
    pattern: 'gig',
    countLabel: '52 offers seeded',
    to: '/market/gigs',
    accent: 'pink',
  },
  {
    id: 'jobs',
    group: 'work',
    label: 'Jobs & opportunities',
    blurb: 'Internships, graduate roles, scholarships and competitions.',
    pattern: 'discovery',
    countLabel: '29 open',
    to: '/market/jobs',
    handedTo: {
      domain: 'Opportunities',
      to: '/opportunities',
      why: 'Your career profile, applications and employer pipeline stages live in Opportunities. Marketplace only surfaces what is open.',
    },
    accent: 'indigo',
  },
  {
    id: 'roommates',
    group: 'live',
    label: 'Roommates',
    blurb: 'Find someone compatible before you commit to a place.',
    pattern: 'discovery',
    countLabel: 'Preference-based',
    to: '/market/roommates',
    handedTo: {
      domain: 'Housing',
      to: '/housing/roommates',
      why: 'Roommate preferences are private and matching is done in Housing. Nothing about them is listed publicly here.',
    },
    accent: 'purple',
  },
]

export const marketSellers: MarketSeller[] = [
  {
    id: 'sel-1',
    handle: '@campusbites',
    displayName: 'Campus Bites',
    kind: 'Campus vendor',
    verified: true,
    rating: 4.8,
    reviewCount: 124,
    responseRateLabel: '95% response rate',
    campus: 'FUTA, Akure',
    hoursLabel: 'Open until 21:00',
  },
  {
    id: 'sel-2',
    handle: '@studyhub',
    displayName: 'StudyHub Books',
    kind: 'Bookstore',
    verified: true,
    rating: 4.7,
    reviewCount: 256,
    responseRateLabel: '98% response rate',
    campus: 'FUTA, Akure',
    hoursLabel: 'Open until 18:00',
  },
  {
    id: 'sel-3',
    handle: '@peakhostel',
    displayName: 'PeakHostel Residences',
    kind: 'Accommodation provider',
    verified: true,
    rating: 4.6,
    reviewCount: 89,
    responseRateLabel: '93% response rate',
    campus: 'FUTA, Akure',
  },
  {
    id: 'sel-4',
    handle: '@techworks',
    displayName: 'TechWorks Studio',
    kind: 'Technical provider',
    verified: true,
    rating: 4.9,
    reviewCount: 173,
    responseRateLabel: '97% response rate',
    campus: 'FUTA, Akure',
  },
  {
    id: 'sel-5',
    handle: '@tolu.a',
    displayName: 'Tolu A.',
    kind: 'Student seller',
    verified: false,
    rating: 4.6,
    reviewCount: 8,
    campus: 'FUTA, Akure',
  },
  {
    id: 'sel-6',
    handle: '@akure.moves',
    displayName: 'Akure Moves',
    kind: 'Transport provider',
    verified: true,
    rating: 4.5,
    reviewCount: 61,
    responseRateLabel: '90% response rate',
    campus: 'FUTA, Akure',
  },
  {
    id: 'sel-7',
    handle: '@ifeoma.m',
    displayName: 'Ifeoma M.',
    kind: 'Student seller',
    verified: true,
    rating: 4.9,
    reviewCount: 33,
    campus: 'FUTA, Akure',
  },
]

const FOOD_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/0a820c45-3461-46c6-af52-033079632169.jpg'
const BOOK_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/431e25ea-fd1b-4110-be5b-ce532207ad41.jpg'
const ROOM_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/848713a3-c088-402e-8193-6210d9b89061.jpg'
const CAD_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/c2783bf6-b994-458f-9f12-e95f39d2422d.jpg'
const TUTOR_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/dbb2f973-9061-4eb8-9ac4-9732bb93b909.jpg'

export const marketItems: MarketItem[] = [
  // Food — ordering pattern.
  {
    id: 'itm-f1',
    category: 'food',
    title: 'Jollof rice & grilled chicken',
    priceNaira: 2500,
    priceUnit: 'each',
    sellerId: 'sel-1',
    imageUrl: FOOD_IMAGE,
    summary: 'Plate of jollof with grilled chicken, plantain and coleslaw.',
    prepMinutes: 25,
    deliveryLabel: 'Delivery 25–35 min · pickup available',
    availability: 'Available',
    rating: 4.6,
    reviewCount: 54,
    tags: ['Meals'],
  },
  {
    id: 'itm-f2',
    category: 'food',
    title: 'Beans & plantain combo',
    priceNaira: 1400,
    priceUnit: 'each',
    sellerId: 'sel-1',
    summary: 'Stewed beans with fried plantain.',
    prepMinutes: 20,
    deliveryLabel: 'Delivery 20–30 min',
    availability: 'Available',
    rating: 4.4,
    reviewCount: 31,
    tags: ['Meals'],
  },
  {
    id: 'itm-f3',
    category: 'food',
    title: 'Weekly groceries bundle',
    priceNaira: 8900,
    priceUnit: 'bundle',
    sellerId: 'sel-1',
    summary: 'Rice, oil, tomatoes, eggs, noodles and seasoning.',
    deliveryLabel: 'Next-day delivery',
    availability: 'Limited',
    rating: 4.5,
    reviewCount: 18,
    tags: ['Groceries'],
  },
  {
    id: 'itm-f4',
    category: 'food',
    title: 'Chilled zobo (1 litre)',
    priceNaira: 900,
    priceUnit: 'each',
    sellerId: 'sel-1',
    summary: 'Hibiscus drink, lightly spiced.',
    deliveryLabel: 'Delivery 20–30 min',
    availability: 'Sold out',
    tags: ['Drinks'],
  },

  // Books — goods pattern, linked to registered courses.
  {
    id: 'itm-b1',
    category: 'books',
    title: 'Mechanics of Materials, 8th edition',
    priceNaira: 9000,
    priceUnit: 'each',
    sellerId: 'sel-2',
    imageUrl: BOOK_IMAGE,
    summary: 'Set text for MEE 302. Minor pencil annotation in chapters 3–5.',
    condition: 'Good',
    courseCodes: ['MEE 302'],
    availability: 'Available',
    rating: 4.7,
    reviewCount: 41,
    tags: ['Textbooks'],
  },
  {
    id: 'itm-b2',
    category: 'books',
    title: 'Fluid Mechanics — used',
    priceNaira: 6500,
    priceUnit: 'each',
    sellerId: 'sel-5',
    summary: 'Cover worn, all pages intact. Collection on campus.',
    condition: 'Fair',
    courseCodes: ['MEE 305'],
    availability: 'Available',
    rating: 4.6,
    reviewCount: 8,
    tags: ['Textbooks', 'Used'],
  },
  {
    id: 'itm-b3',
    category: 'books',
    title: 'MEE 201 past questions, 2018–2025',
    priceNaira: 1800,
    priceUnit: 'each',
    sellerId: 'sel-2',
    summary: 'Compiled and indexed by session. Printed and bound.',
    condition: 'New',
    courseCodes: ['MEE 201'],
    availability: 'Available',
    rating: 4.8,
    reviewCount: 96,
    tags: ['Past questions'],
  },
  {
    id: 'itm-b4',
    category: 'books',
    title: 'Engineering drawing set',
    priceNaira: 4200,
    priceUnit: 'set',
    sellerId: 'sel-2',
    summary: 'Set square, compass, scale rule and drawing board clips.',
    condition: 'New',
    availability: 'Available',
    rating: 4.5,
    reviewCount: 22,
    tags: ['Stationery'],
  },

  // Campus products.
  {
    id: 'itm-p1',
    category: 'products',
    title: 'Scientific calculator (fx-991)',
    priceNaira: 12500,
    priceUnit: 'each',
    sellerId: 'sel-2',
    summary: 'Exam-permitted model. Sealed.',
    condition: 'New',
    availability: 'Available',
    rating: 4.8,
    reviewCount: 64,
    tags: ['Electronics'],
  },
  {
    id: 'itm-p2',
    category: 'products',
    title: 'Reading lamp, rechargeable',
    priceNaira: 5400,
    priceUnit: 'each',
    sellerId: 'sel-5',
    summary: 'Three brightness levels, holds about six hours.',
    condition: 'Like new',
    availability: 'Limited',
    rating: 4.3,
    reviewCount: 11,
    tags: ['Essentials'],
  },

  // Accommodation — property pattern.
  {
    id: 'itm-a1',
    category: 'accommodation',
    title: '2-bedroom apartment, Obakekere',
    priceNaira: 350000,
    priceUnit: 'per year',
    sellerId: 'sel-3',
    imageUrl: ROOM_IMAGE,
    summary: 'Self-contained, tiled, borehole water, prepaid meter.',
    area: 'Obakekere, Akure',
    distanceKm: 1.2,
    availability: 'Available',
    rating: 4.7,
    reviewCount: 12,
    itemVerified: true,
    rooms: 2,
    amenities: ['Running water', 'Prepaid meter', 'Tiled', 'Parking'],
    tags: ['Apartment'],
  },
  {
    id: 'itm-a2',
    category: 'accommodation',
    title: 'Single room in shared hostel',
    priceNaira: 180000,
    priceUnit: 'per year',
    sellerId: 'sel-3',
    summary: 'Shared kitchen and bathroom, four rooms per floor.',
    area: 'South Gate, Akure',
    distanceKm: 0.6,
    availability: 'Limited',
    itemVerified: false,
    rooms: 1,
    amenities: ['Running water', 'Shared kitchen'],
    tags: ['Hostel'],
  },
  {
    id: 'itm-a3',
    category: 'accommodation',
    title: 'Self-contained studio, Ilaramokin road',
    priceNaira: 260000,
    priceUnit: 'per year',
    sellerId: 'sel-3',
    summary: 'One room with its own kitchen and bathroom. Gated compound.',
    area: 'Ilaramokin, Akure',
    distanceKm: 2.4,
    availability: 'Available',
    rating: 4.4,
    reviewCount: 6,
    itemVerified: true,
    rooms: 1,
    amenities: ['Running water', 'Prepaid meter', 'Security', 'Generator'],
    tags: ['Self-contained'],
  },

  // Transport — booking pattern.
  {
    id: 'itm-t1',
    category: 'transport',
    title: 'Campus shuttle, monthly pass',
    priceNaira: 7500,
    priceUnit: 'per month',
    sellerId: 'sel-6',
    summary: 'South Gate to main campus, weekdays, 07:00–19:00.',
    availability: 'Available',
    rating: 4.5,
    reviewCount: 61,
    tags: ['Campus rides'],
  },
  {
    id: 'itm-t2',
    category: 'transport',
    title: 'Moving van with two hands',
    priceNaira: 18000,
    priceUnit: 'from',
    sellerId: 'sel-6',
    summary: 'Within Akure. Price rises with distance and floor count.',
    availability: 'Available',
    rating: 4.4,
    reviewCount: 27,
    tags: ['Moving'],
  },
  {
    id: 'itm-t3',
    category: 'transport',
    title: 'Akure to Lagos, shared seat',
    priceNaira: 12000,
    priceUnit: 'per seat',
    sellerId: 'sel-6',
    summary: 'Departs 06:30 on Fridays. Seven seats, booked in advance.',
    availability: 'Limited',
    rating: 4.3,
    reviewCount: 38,
    tags: ['Inter-campus'],
  },
  {
    id: 'itm-t4',
    category: 'transport',
    title: 'Airport transfer, Akure to Lagos terminal',
    priceNaira: 45000,
    priceUnit: 'per trip',
    sellerId: 'sel-6',
    summary: 'Private car, up to three passengers and luggage.',
    availability: 'Available',
    rating: 4.6,
    reviewCount: 14,
    tags: ['Airport'],
  },
  {
    id: 'itm-t5',
    category: 'transport',
    title: 'Same-day parcel delivery around Akure',
    priceNaira: 1500,
    priceUnit: 'from',
    sellerId: 'sel-6',
    summary: 'Documents and small parcels. Collected and delivered the same day.',
    availability: 'Available',
    rating: 4.2,
    reviewCount: 52,
    tags: ['Delivery'],
  },

  // Services — booking pattern.
  {
    id: 'itm-s1',
    category: 'services',
    title: 'AutoCAD 2D drawing',
    priceNaira: 10000,
    priceUnit: 'per drawing',
    sellerId: 'sel-4',
    imageUrl: CAD_IMAGE,
    summary: 'Dimensioned 2D drawings from your sketches or measurements.',
    availability: 'Available',
    rating: 4.8,
    reviewCount: 21,
    tags: ['Technical'],
  },
  {
    id: 'itm-s2',
    category: 'services',
    title: 'Laptop repair and diagnostics',
    priceNaira: 6000,
    priceUnit: 'from',
    sellerId: 'sel-4',
    summary: 'Diagnostic fee applies whether or not you proceed with the repair.',
    availability: 'Available',
    rating: 4.7,
    reviewCount: 44,
    tags: ['Technical'],
  },
  {
    id: 'itm-s3',
    category: 'services',
    title: 'Project write-up formatting and proofreading',
    priceNaira: 8000,
    priceUnit: 'from',
    sellerId: 'sel-7',
    summary:
      'Formatting, referencing and language. The work must be your own — no writing on your behalf.',
    availability: 'Available',
    rating: 4.9,
    reviewCount: 19,
    tags: ['Academic'],
  },
  {
    id: 'itm-s4',
    category: 'services',
    title: 'Research assistance and literature search',
    priceNaira: 12000,
    priceUnit: 'from',
    sellerId: 'sel-7',
    summary:
      'Finding and organising sources for your topic. Analysis and writing remain yours.',
    availability: 'Available',
    rating: 4.7,
    reviewCount: 11,
    tags: ['Academic'],
  },
  {
    id: 'itm-s5',
    category: 'services',
    title: 'Printing, binding and project finishing',
    priceNaira: 3500,
    priceUnit: 'from',
    sellerId: 'sel-2',
    summary: 'Hard and spiral binding, colour plates, same-day where possible.',
    availability: 'Available',
    rating: 4.6,
    reviewCount: 87,
    tags: ['Academic'],
  },
  {
    id: 'itm-s6',
    category: 'services',
    title: 'Brand and poster design',
    priceNaira: 9000,
    priceUnit: 'from',
    sellerId: 'sel-5',
    summary: 'Logos, event posters and social graphics. Two rounds of changes.',
    availability: 'Available',
    rating: 4.5,
    reviewCount: 23,
    tags: ['Creative'],
  },
  {
    id: 'itm-s7',
    category: 'services',
    title: 'Event videography and editing',
    priceNaira: 40000,
    priceUnit: 'from',
    sellerId: 'sel-5',
    summary: 'Half-day shoot, one edited highlight film and raw footage.',
    availability: 'Limited',
    rating: 4.4,
    reviewCount: 7,
    tags: ['Creative'],
  },
  {
    id: 'itm-s8',
    category: 'services',
    title: 'Web and mobile development',
    sellerId: 'sel-4',
    summary: 'Quoted per project after a scoping conversation. No fixed price.',
    availability: 'Available',
    rating: 4.8,
    reviewCount: 16,
    tags: ['Technical'],
  },
  {
    id: 'itm-s9',
    category: 'services',
    title: 'Bookkeeping for small campus businesses',
    priceNaira: 15000,
    priceUnit: 'per month',
    sellerId: 'sel-7',
    summary: 'Records, invoices and monthly summaries. Not a filing or audit service.',
    availability: 'Available',
    rating: 4.6,
    reviewCount: 9,
    tags: ['Business'],
  },
  {
    id: 'itm-s10',
    category: 'services',
    title: 'Social media management for student businesses',
    priceNaira: 20000,
    priceUnit: 'per month',
    sellerId: 'sel-5',
    summary: 'Content plan, twelve posts a month, replies handled on weekdays.',
    availability: 'Available',
    rating: 4.3,
    reviewCount: 12,
    tags: ['Business'],
  },

  // Gigs — student-to-student.
  {
    id: 'itm-g1',
    category: 'gigs',
    title: 'Engineering mathematics tutoring',
    priceNaira: 3000,
    priceUnit: 'per session',
    sellerId: 'sel-7',
    imageUrl: TUTOR_IMAGE,
    summary: 'One hour, in person or online. MEE 201 and MEE 202.',
    courseCodes: ['MEE 201', 'MEE 202'],
    availability: 'Available',
    rating: 4.9,
    reviewCount: 33,
    tags: ['Tutoring'],
  },
  {
    id: 'itm-g2',
    category: 'gigs',
    title: 'I will design your presentation',
    priceNaira: 5000,
    priceUnit: 'per deck',
    sellerId: 'sel-5',
    summary: 'Up to 15 slides, two rounds of changes.',
    availability: 'Available',
    rating: 4.6,
    reviewCount: 14,
    tags: ['Creative'],
  },
  {
    id: 'itm-g3',
    category: 'gigs',
    title: 'Event and product photography',
    priceNaira: 15000,
    priceUnit: 'per session',
    sellerId: 'sel-5',
    summary: 'Two hours of shooting, 25 edited images.',
    availability: 'Limited',
    rating: 4.5,
    reviewCount: 9,
    tags: ['Creative'],
  },
]

/** Filters offered above every category. Campus scope is the default. */
export const marketFilters = ['All', 'Nearby', 'Popular', 'Verified', 'New'] as const

export function sellerFor(id: string): MarketSeller | undefined {
  return marketSellers.find((seller) => seller.id === id)
}

export function itemsIn(category: string): MarketItem[] {
  return marketItems.filter((item) => item.category === category)
}
